'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Eye, EyeOff, LogIn, Copy, Check } from 'lucide-react';


interface LoginFormData {
  email: string;
  password: string;
  remember: boolean;
}

const DEMO_ACCOUNTS = [
  { role: 'مالك عقار', email: 'owner@aqarmarket.sa', password: 'Owner@2026' },
  { role: 'باحث عن عقار', email: 'seeker@aqarmarket.sa', password: 'Seeker@2026' },
];

interface Props {
  onSwitchToSignup: () => void;
}

export default function LoginForm({ onSwitchToSignup }: Props) {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const { register, handleSubmit, setValue, formState: { errors } } = useForm<LoginFormData>({
    defaultValues: { remember: false },
  });

  const onSubmit = async (data: LoginFormData) => {
    const valid = DEMO_ACCOUNTS.some(a => a.email === data.email && a.password === data.password);
    setLoading(true);
    // TODO: connect to /api/auth/login endpoint
    await new Promise(r => setTimeout(r, 1200));
    setLoading(false);
    if (!valid) {
      toast.error('بيانات غير صحيحة — استخدم الحسابات التجريبية أدناه لتسجيل الدخول');
      return;
    }
    toast.success('تم تسجيل الدخول بنجاح!');
    setTimeout(() => { window.location.href = '/property-listings'; }, 500);
  };

  const autofill = (acc: typeof DEMO_ACCOUNTS[0]) => {
    setValue('email', acc.email);
    setValue('password', acc.password);
  };

  const copyField = (val: string, key: string) => {
    navigator.clipboard.writeText(val);
    setCopiedField(key);
    setTimeout(() => setCopiedField(null), 1500);
  };

  return (
    <div className="bg-white rounded-2xl shadow-card border border-gray-100 p-6">
      <h2 className="text-xl font-700 text-gray-900 mb-1">مرحباً بعودتك</h2>
      <p className="text-sm text-gray-500 mb-5">سجّل دخولك للمتابعة</p>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Email */}
        <div>
          <label className="block text-sm font-600 text-gray-700 mb-1">البريد الإلكتروني</label>
          <input
            type="email"
            placeholder="example@email.com"
            {...register('email', {
              required: 'البريد الإلكتروني مطلوب',
              pattern: { value: /^\S+@\S+\.\S+$/, message: 'صيغة البريد غير صحيحة' },
            })}
            className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 transition-colors text-right ${
              errors.email ? 'border-red-300 bg-red-50' : 'border-gray-200'
            }`}
          />
          {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email.message}</p>}
        </div>

        {/* Password */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm font-600 text-gray-700">كلمة المرور</label>
            <button type="button" className="text-xs text-primary-500 hover:text-primary-700 transition-colors">
              نسيت كلمة المرور؟
            </button>
          </div>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••"
              {...register('password', {
                required: 'كلمة المرور مطلوبة',
                minLength: { value: 6, message: 'كلمة المرور يجب أن تكون 6 أحرف على الأقل' },
              })}
              className={`w-full border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 transition-colors text-right pl-10 ${
                errors.password ? 'border-red-300 bg-red-50' : 'border-gray-200'
              }`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && <p className="text-xs text-red-500 mt-1">{errors.password.message}</p>}
        </div>

        {/* Remember */}
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            {...register('remember')}
            className="w-4 h-4 rounded border-gray-300 text-primary-500 focus:ring-primary-300"
          />
          <span className="text-sm text-gray-600">تذكرني</span>
        </label>

        {/* Submit */}
        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white font-600 rounded-xl transition-all duration-150 active:scale-95 flex items-center justify-center gap-2"
        >
          {loading ? (
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <>
              <LogIn size={16} />
              تسجيل الدخول
            </>
          )}
        </button>
      </form>

      <div className="mt-4 text-center text-sm text-gray-500">
        ليس لديك حساب؟{' '}
        <button onClick={onSwitchToSignup} className="text-primary-500 font-600 hover:text-primary-700 transition-colors">
          أنشئ حساباً مجاناً
        </button>
      </div>

      {/* Demo Credentials */}
      <div className="mt-5 border border-dashed border-gray-200 rounded-xl p-4 bg-gray-50">
        <p className="text-xs font-600 text-gray-500 mb-3 text-center">حسابات تجريبية للتجربة</p>
        <div className="space-y-2">
          {DEMO_ACCOUNTS.map((acc, i) => (
            <div key={`demo-${i}`} className="bg-white rounded-lg border border-gray-100 p-2.5 flex items-center justify-between gap-2">
              <div className="flex-1 min-w-0">
                <span className="text-xs font-700 text-primary-600 block">{acc.role}</span>
                <span className="text-xs text-gray-600 truncate block">{acc.email}</span>
              </div>
              <div className="flex gap-1.5">
                <button
                  type="button"
                  onClick={() => copyField(acc.email, `email-${i}`)}
                  className="p-1.5 bg-gray-100 hover:bg-gray-200 rounded text-gray-500 transition-colors"
                  title="نسخ البريد"
                >
                  {copiedField === `email-${i}` ? <Check size={12} className="text-emerald-600" /> : <Copy size={12} />}
                </button>
                <button
                  type="button"
                  onClick={() => autofill(acc)}
                  className="px-2.5 py-1.5 bg-primary-50 hover:bg-primary-100 text-primary-600 text-xs font-600 rounded transition-colors"
                >
                  استخدام
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}